import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CallType, ScribeJobStatus } from '../services/scribe.service'

export interface RecordingState {
  // Recording state
  isRecording: boolean
  isPaused: boolean
  isUploading: boolean
  isProcessing: boolean

  // Patient context
  patientId: string | null
  patientRefId: string | null
  patientName: string | null

  // Call configuration
  callType: CallType | null
  gender: string | null

  // Recording data
  duration: number

  // Job tracking
  jobId: string | null
  jobStatus: ScribeJobStatus | null
  jobProgress: number

  // Error state
  error: string | null

  // UI state
  miniRecorderVisible: boolean
  miniRecorderMinimized: boolean
}

const initialState: RecordingState = {
  isRecording: false,
  isPaused: false,
  isUploading: false,
  isProcessing: false,
  patientId: null,
  patientRefId: null,
  patientName: null,
  callType: null,
  gender: null,
  duration: 0,
  jobId: null,
  jobStatus: null,
  jobProgress: 0,
  error: null,
  miniRecorderVisible: false,
  miniRecorderMinimized: false,
}

export interface StartRecordingPayload {
  patientId: string
  patientRefId: string
  patientName: string
  callType: CallType
  gender?: string
}

export const recordingSlice = createSlice({
  name: 'recording',
  initialState,
  reducers: {
    startRecording: (state, action: PayloadAction<StartRecordingPayload>) => {
      state.isRecording = true
      state.isPaused = false
      state.isUploading = false
      state.isProcessing = false
      state.patientId = action.payload.patientId
      state.patientRefId = action.payload.patientRefId
      state.patientName = action.payload.patientName
      state.callType = action.payload.callType
      state.gender = action.payload.gender || null
      state.duration = 0
      state.jobId = null
      state.jobStatus = null
      state.jobProgress = 0
      state.error = null
      state.miniRecorderVisible = true
      state.miniRecorderMinimized = false
    },

    pauseRecording: (state) => {
      state.isPaused = true
    },

    resumeRecording: (state) => {
      state.isPaused = false
    },

    stopRecording: (state) => {
      state.isRecording = false
      state.isPaused = false
    },

    updateDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload
    },

    setUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload
    },

    setJobInfo: (state, action: PayloadAction<{ jobId: string; status: ScribeJobStatus }>) => {
      state.jobId = action.payload.jobId
      state.jobStatus = action.payload.status
      state.isUploading = false
      state.isProcessing = true
    },

    updateJobStatus: (state, action: PayloadAction<{ status: ScribeJobStatus; progress: number }>) => {
      state.jobStatus = action.payload.status
      state.jobProgress = action.payload.progress
      if (action.payload.status === 'completed' || action.payload.status === 'failed') {
        state.isProcessing = false
      }
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isRecording = false
      state.isPaused = false
      state.isUploading = false
      state.isProcessing = false
    },

    clearError: (state) => {
      state.error = null
    },

    toggleMiniRecorder: (state) => {
      state.miniRecorderVisible = !state.miniRecorderVisible
    },

    showMiniRecorder: (state) => {
      state.miniRecorderVisible = true
    },

    hideMiniRecorder: (state) => {
      state.miniRecorderVisible = false
    },

    minimizeMiniRecorder: (state) => {
      state.miniRecorderMinimized = true
    },

    expandMiniRecorder: (state) => {
      state.miniRecorderMinimized = false
    },

    toggleMinimized: (state) => {
      state.miniRecorderMinimized = !state.miniRecorderMinimized
    },

    resetRecording: (state) => {
      return { ...initialState }
    },

    // Reset but keep mini-recorder visible for new recording
    resetForNewRecording: (state) => {
      state.isRecording = false
      state.isPaused = false
      state.isUploading = false
      state.isProcessing = false
      state.duration = 0
      state.jobId = null
      state.jobStatus = null
      state.jobProgress = 0
      state.error = null
      // Keep patient context and mini-recorder visible
    },
  },
})

export const {
  startRecording,
  pauseRecording,
  resumeRecording,
  stopRecording,
  updateDuration,
  setUploading,
  setJobInfo,
  updateJobStatus,
  setError,
  clearError,
  toggleMiniRecorder,
  showMiniRecorder,
  hideMiniRecorder,
  minimizeMiniRecorder,
  expandMiniRecorder,
  toggleMinimized,
  resetRecording,
  resetForNewRecording,
} = recordingSlice.actions

export default recordingSlice.reducer
